import {
  Component,
  HostBinding,
  OnInit,
  ViewEncapsulation,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IntervalBase, PaymentInterval } from '@fmnts/api/next';
import { DecimalInputDirective } from '@fmnts/components/numeric';
import { FmntsRadioGroup, RadioCardComponent } from '@fmnts/components/radio';
import { Maybe, isNil, optionHasValue } from '@fmnts/core';
import * as Decimal from '@fmnts/core/decimal';
import { InlineTranslateable } from '@fmnts/i18n';
import { LetDirective } from '@ngrx/component';
import { Option, String } from 'effect';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import {
  AbstractAmountFieldComponent,
  AmountFieldValue,
  AmountFieldValueEquivalence,
  AmountFieldValueFromUnknown,
  AmountProposalItem,
} from './abstract-donation-amount-field';

function findWithValue(needle: Decimal.Decimal) {
  return ({ value }: AmountProposalItem) =>
    !isNil(value) && needle.equals(value);
}

interface IntervalAmount {
  amount: Decimal.Decimal;
  interval: PaymentInterval;
}

/**
 * Field component for displaying amount proposals and
 * input fields for entering a custom amount.
 */
@Component({
  selector: 'next-multi-interval-amount-field',
  templateUrl: './multi-interval-amount-field.component.html',
  styleUrl: './multi-interval-amount-field.component.scss',
  standalone: true,
  imports: [
    SharedDeclarativeCampaignFormsModule,
    DecimalInputDirective,
    FmntsRadioGroup,
    RadioCardComponent,
    LetDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class MultiIntervalAmountFieldComponent
  extends AbstractAmountFieldComponent
  implements OnInit
{
  @HostBinding('class') componentClass =
    'form-field form-field__donation-amount';

  /**
   * Value of the donation amount field.
   */
  get value(): AmountFieldValue {
    return this._value();
  }
  set value(value: AmountFieldValue) {
    if (AmountFieldValueEquivalence(value, this._value())) {
      return;
    }
    this._value.set(value);
    this._onChange(this._value());
    this._updateInputsFromCurrentValue();
  }

  /** Value of the selected radio group item. */
  protected get selectedOption() {
    return Option.getOrNull(this._selectedOption());
  }
  protected set selectedOption(value: Decimal.Numeric | null) {
    this._selectedOption.set(Option.fromNullable(value));
    this.value = AmountFieldValueFromUnknown(value);
    this._onTouched();
  }
  private readonly _selectedOption = signal<Option.Option<Decimal.Numeric>>(
    Option.none(),
  );

  /** `true` if a proposal is selected. */
  private readonly proposalSelected = computed(() =>
    Option.isSome(this._selectedOption()),
  );

  /** Amount per month. */
  protected get monthlyAmount() {
    return Option.getOrNull(this._monthlyAmount());
  }
  protected set monthlyAmount(value: AmountFieldValue) {
    this._monthlyAmount.set(Option.fromNullable(value));
    this._updateValueFromInput(this._monthlyAmount(), PaymentInterval.Monthly);
    this._selectedOption.set(Option.none());
  }
  private readonly _monthlyAmount = signal<Option.Option<Decimal.Decimal>>(
    Option.none(),
    { equal: Option.getEquivalence(Decimal.Equivalence) },
  );

  /** Amount per year. */
  protected get yearlyAmount() {
    return Option.getOrNull(this._yearlyAmount());
  }
  protected set yearlyAmount(value: AmountFieldValue) {
    this._yearlyAmount.set(Option.fromNullable(value));
    this._updateValueFromInput(this._yearlyAmount(), PaymentInterval.Yearly);
    this._selectedOption.set(Option.none());
  }
  private readonly _yearlyAmount = signal<Option.Option<Decimal.Decimal>>(
    Option.none(),
    { equal: Option.getEquivalence(Decimal.Equivalence) },
  );

  /**
   * The interval that the field value should be tied to.
   *
   * As long as the field isn't initialized, it doesn't have a value.
   */
  protected readonly _fieldInterval = signal<Option.Option<PaymentInterval>>(
    Option.none(),
    { equal: Option.getEquivalence(String.Equivalence) },
  );

  protected _labelYearlyInput: Maybe<InlineTranslateable>;
  protected _labelMonthlyInput: Maybe<InlineTranslateable>;

  /** Classes for amount input fields. */
  protected readonly _inputFieldClass = 'w-full md:w-1/2';
  /** Classes for radio cards. */
  protected readonly _radioCardClass = 'w-1/2 sm:w-1/4';

  ngOnInit() {
    this._labelYearlyInput = this.field.attrs?.label_yearly;
    this._labelMonthlyInput = this.field.attrs?.label_monthly;
    this._setupInputFields();

    // After the base interval for the field is setup, the according values must be updated as well.
    this._updateInputsFromCurrentValue();

    // This Observable keeps the donation amount in sync with
    // the available items. This might be needed as the
    // previously selected proposal might no longer be
    // available, so the amount will be unset.
    this.items$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filteredItems) => {
        this.syncFromSelectedProposal(filteredItems);
      });
  }

  /**
   * Sets the model value. Implemented as part of `ControlValueAccessor`.
   *
   * @param newValue
   */
  writeValue(newValue: unknown) {
    this._value.set(AmountFieldValueFromUnknown(newValue));
    this._selectedOption.set(Option.none());
    this._updateInputsFromCurrentValue();
  }

  /**
   * Updates the field value from changing an input.
   * @param amount New value
   * @param inputInterval Interval associated with the input
   */
  private _updateValueFromInput(
    amount: Option.Option<Decimal.Decimal>,
    inputInterval: PaymentInterval,
  ) {
    const fieldValue = Option.map(
      Option.all({
        amount,
        interval: Option.fromNullable(inputInterval),
      }),
      this._amountToTargetInterval(Option.getOrThrow(this._fieldInterval())),
    );

    this._value.set(Option.getOrNull(fieldValue));
    this._onChange(this._value());
    this._syncOtherInput(amount, inputInterval);
  }

  /**
   * Updates the input values from the current field value.
   */
  private _updateInputsFromCurrentValue() {
    const intervalAmount = Option.all({
      amount: Option.fromNullable(this.value),
      interval: this._fieldInterval(),
    });

    this._monthlyAmount.set(
      Option.map(
        intervalAmount,
        this._amountToTargetInterval(PaymentInterval.Monthly),
      ),
    );
    this._yearlyAmount.set(
      Option.map(
        intervalAmount,
        this._amountToTargetInterval(PaymentInterval.Yearly),
      ),
    );
  }

  /**
   * Syncs the other input field based on the.
   *
   * @param value New value
   * @param inputInterval Interval associated with the input
   */
  private _syncOtherInput(
    value: Option.Option<Decimal.Decimal>,
    inputInterval: PaymentInterval,
  ) {
    const target =
      inputInterval === PaymentInterval.Monthly
        ? PaymentInterval.Yearly
        : PaymentInterval.Monthly;

    const targetValue = Option.map(value, (amount) =>
      this.donationAmountService.transform(amount, inputInterval, target),
    );

    switch (target) {
      case PaymentInterval.Monthly:
        this._monthlyAmount.set(targetValue);
        break;
      case PaymentInterval.Yearly:
        this._yearlyAmount.set(targetValue);
        break;
    }
  }

  /**
   * Creates a function that can be used to convert an amount+interval
   * to a new interval.
   *
   * @param target The interval that the amount should be converted to.
   */
  private _amountToTargetInterval(target: PaymentInterval) {
    return ({ amount, interval }: IntervalAmount): Decimal.Decimal =>
      interval === target
        ? amount
        : this.donationAmountService.transform(amount, interval, target);
  }

  private _setupInputFields(base = this.getCalculationBase()) {
    switch (base) {
      case IntervalBase.Monthly:
        this._fieldInterval.set(Option.some(PaymentInterval.Monthly));
        break;
      case IntervalBase.Yearly:
        this._fieldInterval.set(Option.some(PaymentInterval.Yearly));
        break;
      default:
        throw new Error(
          `The multi interval amount field can not be used with the given base interval ${base}`,
        );
    }
  }

  /**
   * Updates `value` based on selected proposal and available items.
   *
   * @param e Change event
   * @param items Available items
   */
  private syncFromSelectedProposal(items: AmountProposalItem[]) {
    if (!this.proposalSelected() || !optionHasValue(this.value)) {
      // Nothing to do, if we're not dealing with a selected proposal
      return;
    }

    if (!items.some(findWithValue(this.value))) {
      // Reset bc previously selected item is no longer available
      this._selectedOption.set(Option.none());
      this.value = null;
    }
  }
}
